export type ScalarAttributeValue = string | number | boolean | null;

const isScalar = (value: unknown): value is ScalarAttributeValue =>
    value === null || ['string', 'number', 'boolean'].includes(typeof value);

/**
 * Safe-by-default sink filter (RUN-814).
 *
 * `AnalyticsEventAttributes` is an open `unknown` map, so any emitter can hand a sink a whole
 * object — a member record, a form's raw values, a free-text feedback blob — and it reaches
 * Amplitude verbatim, arriving in the taxonomy as dotted `parent.child` property names. Filtering
 * on the *shape* of the value at the one choke point every emitter passes through means no call
 * site can leak an object by forgetting to flatten it first. Deliberately not a denylist of
 * known-bad keys: RUN-740 leaked through a plainly-named string, so names carry no signal.
 *
 * `null` is kept as a meaningful "unset"; `undefined`, objects, arrays and functions are dropped.
 * `sinkOwnedKeys` are dropped whatever their value, so a caller can never set a key the sink
 * computes itself (a normalised `path`, a derived `pageName`).
 */
export const dropNonScalarAttributes = (
    attributes: unknown,
    sinkOwnedKeys: readonly string[] = []
): Record<string, ScalarAttributeValue> => {
    const owned = new Set(sinkOwnedKeys);
    // Null-prototype map, so a caller-supplied `__proto__` is an own property rather than a
    // prototype mutation even when its value is scalar.
    const safe: Record<string, ScalarAttributeValue> = Object.create(null);

    // Typed `unknown` rather than the attribute map: the partner sink's declared type admits a
    // whole `Location`, which has no index signature, and legacy call sites pass no attributes at
    // all so this arrives `undefined` at runtime — `Object.entries(undefined)` would throw and the
    // caller's catch would silently drop the whole event.
    //
    // An array is excluded rather than treated as an object: `Object.entries(['a', 'b'])` yields
    // `{ 0: 'a', 1: 'b' }`, which would put index-named properties into the taxonomy — the same
    // schema pollution as the UUID-keyed property names in RUN-621.
    const isMap = typeof attributes === 'object' && attributes !== null && !Array.isArray(attributes);
    const source = isMap ? attributes : {};

    for (const [key, value] of Object.entries(source)) {
        if (!owned.has(key) && isScalar(value)) {
            safe[key] = value;
        }
    }

    return safe;
};
